import type {
  ChatSessionContextAnchor,
  EntityType,
  Message,
  TimelineEventCategory,
  TimelineEventDTO,
} from '#types/types';

const MAX_SESSION_SNIPPET_LENGTH = 140;

const TIMELINE_EVENT_CATEGORIES: readonly TimelineEventCategory[] = [
  'COMMUNICATION',
  'AGENTIC',
  'MUTATION',
  'TELEMETRY',
];

const TIMELINE_ACTION_SYNC_CATEGORIES = [
  'TELEMETRY',
  'AGENTIC',
] as const;

export type TimelineActionSyncCategory = (typeof TIMELINE_ACTION_SYNC_CATEGORIES)[number];

export const isTimelineEventCategory = (value: unknown): value is TimelineEventCategory =>
  typeof value === 'string' &&
  TIMELINE_EVENT_CATEGORIES.includes(value as TimelineEventCategory);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const normalizeAnchorMass = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return Math.max(1, Math.floor(value));
};

export const messageContentToText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((part) => messageContentToText(part))
      .filter((part) => part.trim().length > 0)
      .join(' ')
      .trim();
  }

  if (isRecord(value)) {
    const directText =
      typeof value.text === 'string'
        ? value.text
        : typeof value.content === 'string'
          ? value.content
          : typeof value.value === 'string'
            ? value.value
            : null;

    if (directText !== null) {
      return directText;
    }

    const nested =
      value.text !== undefined
        ? value.text
        : value.content !== undefined
          ? value.content
          : value.value !== undefined
            ? value.value
            : null;

    if (nested !== null) {
      return messageContentToText(nested);
    }
  }

  return '';
};

export const normalizeSnippetText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed.slice(0, MAX_SESSION_SNIPPET_LENGTH) : null;
};

export const getTimelineMessageSnippet = (payload: unknown): string | null => {
  if (!isRecord(payload)) return null;
  return normalizeSnippetText(messageContentToText(payload.content));
};

const isMessageRole = (value: unknown): value is Message['role'] =>
  value === 'assistant' || value === 'user' || value === 'system';

export const parseTimelineMessagePayload = (payload: unknown): Message | null => {
  if (!isRecord(payload)) return null;
  if (!isMessageRole(payload.role)) return null;
  if (typeof payload.timestamp !== 'string' || payload.timestamp.trim().length === 0) return null;

  return {
    ...(payload as unknown as Message),
    role: payload.role,
    timestamp: payload.timestamp,
    content: messageContentToText(payload.content),
  };
};

export const parseTimelineActionPayload = (payload: unknown): Record<string, unknown> => {
  if (!isRecord(payload)) return {};
  return payload;
};

export const mapTimelineEventToDTO = (row: any): TimelineEventDTO => {
  const payload = isRecord(row?.payload) ? row.payload : {};
  const category = isTimelineEventCategory(row?.category) ? row.category : 'TELEMETRY';

  return {
    id: typeof row?.id === 'string' ? row.id : '',
    organisationId: typeof row?.organisationId === 'string' ? row.organisationId : '',
    userId: typeof row?.userId === 'string' ? row.userId : null,
    chatSessionId: typeof row?.chatSessionId === 'string' ? row.chatSessionId : null,
    targetEntityType: normalizeNonEmptyString(row?.targetEntityType),
    targetEntityId: normalizeNonEmptyString(row?.targetEntityId),
    category,
    eventType: typeof row?.eventType === 'string' ? row.eventType : '',
    payload,
    createdAt:
      row?.createdAt instanceof Date
        ? row.createdAt.toISOString()
        : typeof row?.createdAt === 'string'
          ? row.createdAt
          : new Date(0).toISOString(),
    expiresAt:
      row?.expiresAt instanceof Date
        ? row.expiresAt.toISOString()
        : typeof row?.expiresAt === 'string'
          ? row.expiresAt
          : null,
  };
};

const ANCHOR_EVENT_TYPES = new Set(['ENTITY_ANCHORED', 'ENTITY_UNANCHORED']);
const DEFAULT_CONTEXT_SUMMARY_SIZE = 12;

type TimelineAnchorEvent = {
  eventType: string;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
  payload: unknown;
};

type TimelineAnchorEventWithSession = TimelineAnchorEvent & {
  chatSessionId?: string | null;
};

const applyAnchorEventToSummary = (
  summaryMap: Map<string, ChatSessionContextAnchor>,
  event: TimelineAnchorEvent
): void => {
  if (!ANCHOR_EVENT_TYPES.has(event.eventType)) return;

  const payload = parseTimelineActionPayload(event.payload);
  const entityType =
    normalizeNonEmptyString(payload.entityType) ??
    normalizeNonEmptyString(event.targetEntityType);
  const entityId =
    normalizeNonEmptyString(payload.entityId) ??
    normalizeNonEmptyString(event.targetEntityId);

  if (!entityType || !entityId) return;

  const key = `${entityType}:${entityId}`;
  if (event.eventType === 'ENTITY_UNANCHORED') {
    summaryMap.delete(key);
    return;
  }

  const name = normalizeNonEmptyString(payload.name) ?? entityId;
  summaryMap.set(key, {
    entityType: entityType as EntityType,
    entityId,
    name,
    mass: normalizeAnchorMass(payload.mass),
  });
};

const summarizeAnchorMap = (
  summaryMap: Map<string, ChatSessionContextAnchor>,
  maxEntries: number
): ChatSessionContextAnchor[] => {
  return Array.from(summaryMap.values())
    .sort((left, right) => {
      if (right.mass !== left.mass) return right.mass - left.mass;
      if (left.name !== right.name) return left.name.localeCompare(right.name);
      if (left.entityType !== right.entityType) return left.entityType.localeCompare(right.entityType);
      return left.entityId.localeCompare(right.entityId);
    })
    .slice(0, Math.max(1, maxEntries));
};

export const buildContextSummaryFromEvents = (
  events: TimelineAnchorEvent[],
  maxEntries: number = DEFAULT_CONTEXT_SUMMARY_SIZE
): ChatSessionContextAnchor[] => {
  const summaryMap = new Map<string, ChatSessionContextAnchor>();
  events.forEach((event) => applyAnchorEventToSummary(summaryMap, event));
  return summarizeAnchorMap(summaryMap, maxEntries);
};

export const buildContextSummaryBySessionFromEvents = (
  events: TimelineAnchorEventWithSession[],
  maxEntries: number = DEFAULT_CONTEXT_SUMMARY_SIZE
): Map<string, ChatSessionContextAnchor[]> => {
  const summaryBySession = new Map<string, Map<string, ChatSessionContextAnchor>>();

  events.forEach((event) => {
    const sessionId = normalizeNonEmptyString(event.chatSessionId);
    if (!sessionId) return;

    let summaryMap = summaryBySession.get(sessionId);
    if (!summaryMap) {
      summaryMap = new Map<string, ChatSessionContextAnchor>();
      summaryBySession.set(sessionId, summaryMap);
    }
    applyAnchorEventToSummary(summaryMap, event);
  });

  const result = new Map<string, ChatSessionContextAnchor[]>();
  summaryBySession.forEach((summaryMap, sessionId) => {
    const summary = summarizeAnchorMap(summaryMap, maxEntries);
    if (summary.length > 0) {
      result.set(sessionId, summary);
    }
  });

  return result;
};
